import type {
  HealthStatus,
  HistoryEntry,
  LogEntry,
  OrchestratorSchemas,
  Session,
  User,
} from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { normalizeList, type Listed } from '../page.ts'
import { createQuery, queryParams } from '../query.ts'
import type { PageArgs } from './ops.ts'

/** 관리·로그 도메인 조회 — 사용자 · 세션 · 로그 · 이력 · 헬스 */

export interface UserListArgs extends PageArgs {
  /** ⚠ 계약 enum 3종뿐이다. 화면의 `service` 는 여기 못 넣는다 */
  role?: 'admin' | 'operator' | 'viewer'
  status?: NonNullable<User['status']>
}

export const useUsers = createQuery<UserListArgs, Listed<User>>({
  key: (args) => keys.users.list(args),
  fetch: async (args) =>
    normalizeList<User>(
      await orchestrator.request({ method: 'GET', path: '/users', query: queryParams(args) }),
      args,
    ),
})

export const useUser = createQuery<{ userId: string | undefined }, User>({
  key: ({ userId }) => keys.users.detail(userId ?? ''),
  fetch: ({ userId }) =>
    orchestrator.request<User>({ method: 'GET', path: `/users/${String(userId)}` }),
  enabled: ({ userId }) => userId !== undefined && userId !== '',
})

/**
 * 한 계정의 실효 세션 만료 — `GET /users/{user_id}/session-policy`(`IFC-RO-89` · `UAM-SES-06` · `07` §4.7 🔵 ⓑ).
 *
 * 실물(`ctcells-core/app/views/session_policy.py` · core@d50b8cf4 · dev2 실측 2026-10-12)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 역할 | **admin 한정**(`ALLOWED_ROLES`) — operator · viewer 는 403. 제어락 비대상 |
 * | 응답 | `EffectiveSessionPolicy` — 세 칸(`access_ttl_sec` · `idle_ttl_sec` · `absolute_ttl_sec`)마다 `{ value(초), source }` |
 * | 출처 | `user` · `role` · `default` · `base`(= 기본값). 칸마다 계정 → 역할 → 전체 → 기본값 순으로 먼저 있는 단 |
 * | 경고 | `below_unattended_cycle` — 실효 절대 상한 < 무인 사이클. **서버 판정**이라 화면은 그대로 그린다 |
 * | 없음 | 계정이 없으면 404 `NOT_FOUND` |
 *
 * ⚠ 조합이 틀린 계정은 서버가 «로그인 때 실제로 쓰일 값»(전부 기본값)으로 바꿔 준다 — 화면이 다시 판정하지 않는다.
 * 🔴 경로의 `{user_id}` 는 **`user_id`** 다(`username` 아님 — 실물에 UUID 와 `dev-admin` 이 섞여 있다).
 * 🔴 admin 이 아니면 컨테이너가 `skip` 한다 — 403 이 날 요청을 보내지 않는다.
 */
export type EffectiveSessionPolicy = OrchestratorSchemas['schemas']['EffectiveSessionPolicy']

export const useUserSessionPolicy = createQuery<
  { userId: string | undefined; skip?: boolean },
  EffectiveSessionPolicy
>({
  key: ({ userId }) => keys.users.sessionPolicy(userId ?? ''),
  fetch: ({ userId }) =>
    orchestrator.request<EffectiveSessionPolicy>({
      method: 'GET',
      path: `/users/${encodeURIComponent(String(userId))}/session-policy`,
    }),
  enabled: ({ userId }) => userId !== undefined && userId !== '',
})

/** ⚠ `Session.client`(접속 기기)가 실물에 오지 않는다 — 화면 열이 빈다(보고 대상 K) */
export const useSessions = createQuery<PageArgs & { user_id?: string }, Listed<Session>>({
  key: (args) => keys.auth.sessions(args),
  fetch: async (args) =>
    normalizeList<Session>(
      await orchestrator.request({ method: 'GET', path: '/sessions', query: queryParams(args) }),
      args,
    ),
})

export interface LogListArgs extends PageArgs {
  level?: LogEntry['level']
  source?: string
  /** ISO 8601. 서버가 문자열로 받는다 */
  from?: string
  to?: string
}

/**
 * ⚠ 실물 **308,852건**인데 **정렬 파라미터가 없다**(실측). 서버 기본 정렬에 의존하고,
 *   그 기준이 스펙에 적혀 있지 않다. 화면 열 정렬은 현재 페이지 안에서만 된다.
 */
export const useLogs = createQuery<LogListArgs, Listed<LogEntry>>({
  key: (args) => keys.logs.list(args),
  fetch: async (args) =>
    normalizeList<LogEntry>(
      await orchestrator.request({ method: 'GET', path: '/logs', query: queryParams(args) }),
      args,
    ),
})

export interface HistoryListArgs extends PageArgs {
  event_type?: HistoryEntry['event_type']
  /**
   * 서버가 **세 원장 모두에** 건다(`app/services/history.py` `list_` · 실측 2026-09-28).
   * ⚠ 종전 화면들은 이 칸이 없던 때의 것이라 받아서 `batch:<id>` 로 거른다(`historyForBatch`).
   */
  batch_id?: string
  from?: string
  to?: string
}

export const useHistory = createQuery<HistoryListArgs, Listed<HistoryEntry>>({
  key: (args) => keys.history.list(args),
  fetch: async (args) =>
    normalizeList<HistoryEntry>(
      await orchestrator.request({ method: 'GET', path: '/history', query: queryParams(args) }),
      args,
    ),
})

/**
 * 화면이 「이벤트」라고 부르는 원장 둘 — `event`(`event_log`) · `batch_record`(L2 배치 기록).
 *
 * 🔴 **2026-10-08(v0.9.4) 서버가 배치 기록을 `event_log` 에서 뗐다**(`ctcells-core` `app/services/batch_records.py` 머리말 ·
 *    정본 `05` `DM-REL-66`). 발송 · 실행 종착 · 실행 관측 · 배치 전이 · 멈춤 사유 · 재개 거부 · 진행 사본이 이제
 *    `batch_record` 에 산다. 옛 배치의 행은 `event` 에 남아 있다(옮기지 않았다 · 실측 2026-10-11 dev2 — 옛 배치
 *    `event` 406건 · `batch_record` 0건). ⇒ **둘 다 읽어야** 옛 작업도 새 작업도 이벤트가 선다.
 * ⚠ `/history` 의 `event_type` 은 값 하나만 받는다(`app/views/history.py` `_EVENT_TYPE_LITERAL` · 복수 인자 없음).
 *   빼면 감사 · 명령까지 넷이 다 와서 `limit` 를 나눠 먹는다 — 그래서 원장마다 한 번씩 묻는다.
 */
export const EVENT_LEDGERS = ['event', 'batch_record'] as const satisfies readonly HistoryLedger[]

/**
 * **한 작업의** 이벤트 원장 셋 — 위 둘 + 감사(`audit`). 상시 작업 줄 이벤트 칸 · 작업별 이벤트 로그 CSV 가 쓴다.
 *
 * 🔴 **사람이 누른 시작 · 일시정지 · 재개 · 중지 · 복구 판정은 감사 원장에만 있다**(팀장 결정 2026-10-11).
 *    `batch_record` 의 배치 전이(`batch_state_change`)는 «시스템이 일으킨» 전이만 적는다(`ctcells-core`
 *    `batches._record_state_change` 「사람 경로(화면)는 넘기지 않아 행이 없다」). 실물 `c7c362be` 는 사람이 중지해 aborted 로
 *    끝났는데 두 원장에 전이 행이 0이었다 — 「이 작업 왜 끝났지」에 답이 없었다.
 * 🔴 감사 원장도 서버의 `batch_id` 필터로 거른다 — 서버가 `audit_log.target` 의 `batch:` 접두를 벗겨 그 작업 것만
 *    준다(`app/services/history.py` `_batch_key_audit`). `target` 인자는 `/history` 에 없다(openapi 확인) — 받아서 거르지 않는다.
 * ⚠ 로그 화면 이벤트 탭은 이것을 쓰지 않는다 — 거기엔 감사 탭이 따로 있다(`EVENT_LEDGERS`).
 */
export const BATCH_EVENT_LEDGERS = [
  'event',
  'batch_record',
  'audit',
] as const satisfies readonly HistoryLedger[]

/** 원장 이름(`HistoryEntry.event_type`) */
export type HistoryLedger = NonNullable<HistoryEntry['event_type']>

/** 원장 여럿을 묻는 인자 — `event_type` 대신 `ledgers`(기본 `EVENT_LEDGERS`)로 고른다 */
export type EventHistoryArgs = Omit<HistoryListArgs, 'event_type'> & {
  ledgers?: readonly HistoryLedger[]
}

/** 원장을 합친 조회의 캐시 키 — 구독 무효화(`useBatchEventsRefresh`)가 같은 키를 친다 */
export function eventHistoryKey(args: EventHistoryArgs) {
  return keys.history.list({ ...args, ledgers: args.ledgers ?? EVENT_LEDGERS })
}

/**
 * 원장 여럿을 **시각 내림차순**(서버 차례와 같다)으로 한 목록에 섞는다.
 * 같은 시각은 넘긴 차례(앞 목록 먼저) · 목록 안 차례를 지킨다. 시각을 못 읽는 행은 맨 뒤.
 */
export function mergeHistoryDesc(lists: readonly (readonly HistoryEntry[])[]): HistoryEntry[] {
  return lists
    .flatMap((list, at) => list.map((entry, index) => ({ entry, at, index })))
    .map((row) => ({ ...row, ms: Date.parse(row.entry.ts) }))
    .sort(
      (a, b) =>
        (Number.isNaN(b.ms) ? -Infinity : b.ms) - (Number.isNaN(a.ms) ? -Infinity : a.ms) ||
        a.at - b.at ||
        a.index - b.index,
    )
    .map((row) => row.entry)
}

/**
 * 「이벤트」 원장들(`ledgers` · 기본 `EVENT_LEDGERS`)을 한 목록으로 — 로그 화면 이벤트 탭(둘) · 상시 작업 줄 이벤트 칸
 * (`BATCH_EVENT_LEDGERS` 셋)이 쓴다.
 *
 * - 원장마다 같은 인자(`batch_id` · `from` · `to` · `limit`)로 **한 번씩** 묻고 시각 내림차순으로 섞는다
 * - `limit` 를 주면 섞은 뒤 위에서 `limit` 행만 남긴다 — 둘 다 최근 `limit` 행을 받았으므로 섞인 최근 `limit` 행은
 *   빠짐없이 그 안에 든다. ⚠ `offset` 은 원장별로 걸려 «섞인 목록의 쪽»이 아니다 — 쪽 넘김에 쓰지 마라
 * - `total_count` 는 합(하나라도 모르면 `null`)
 * - 🔴 **하나라도 실패하면 조회째 실패다.** 한쪽만 보이면 「이 작업의 이벤트가 이것뿐」으로 읽힌다 — 세 원장은
 *   같은 권한(operator · admin · `views/history.py` `PRIVILEGED_ROLES`)이라 같이 산다/죽는다
 * - 폴링이 아니다 — 스냅샷 1회 + 구독 무효화(`eventHistoryKey`)
 */
export async function fetchEventHistory(
  args: EventHistoryArgs,
  fetchPage: (args: HistoryListArgs) => Promise<Listed<HistoryEntry>> = fetchHistoryPage,
): Promise<Listed<HistoryEntry>> {
  const { ledgers = EVENT_LEDGERS, ...rest } = args
  const pages = await Promise.all(ledgers.map((event_type) => fetchPage({ ...rest, event_type })))
  const merged = mergeHistoryDesc(pages.map((page) => page.items))
  const totals = pages.map((page) => page.total_count)
  return {
    items: args.limit === undefined ? merged : merged.slice(0, args.limit),
    total_count: totals.some((total) => total === null)
      ? null
      : totals.reduce<number>((sum, total) => sum + (total ?? 0), 0),
    limit: args.limit ?? null,
    offset: args.offset ?? null,
  }
}

async function fetchHistoryPage(args: HistoryListArgs): Promise<Listed<HistoryEntry>> {
  return normalizeList<HistoryEntry>(
    await orchestrator.request({ method: 'GET', path: '/history', query: queryParams(args) }),
    args,
  )
}

export const useEventHistory = createQuery<EventHistoryArgs, Listed<HistoryEntry>>({
  key: eventHistoryKey,
  fetch: (args) => fetchEventHistory(args),
})

/** `/healthz` 는 인증 없이도 200 이다. 연결 확인용 */
export const useHealth = createQuery<void, HealthStatus>({
  key: () => keys.health.live(),
  fetch: () => orchestrator.request<HealthStatus>({ method: 'GET', path: '/healthz' }),
})
